import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "@/components/admin-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Loader2 } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

interface FaqItem {
  id: number;
  question: string;
  videoId: string;
  sortOrder: number;
  visible: boolean;
  createdAt: string;
  updatedAt: string;
}

const faqFormSchema = z.object({
  question: z.string().min(1, "Question is required"),
  videoId: z.string().min(1, "YouTube video ID is required"),
  visible: z.boolean().default(true),
});

type FaqFormData = z.infer<typeof faqFormSchema>;

function FaqForm({ 
  defaultValues,
  onSubmit,
  isSubmitting 
}: { 
  defaultValues?: FaqFormData;
  onSubmit: (data: FaqFormData) => void;
  isSubmitting: boolean;
}) {
  const form = useForm<FaqFormData>({
    resolver: zodResolver(faqFormSchema),
    defaultValues: defaultValues || {
      question: "",
      videoId: "",
      visible: true,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="question"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Question</FormLabel>
              <FormControl>
                <Input {...field} className="bg-zinc-800 border-zinc-700" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="videoId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>YouTube Video ID</FormLabel>
              <FormControl>
                <Input {...field} className="bg-zinc-800 border-zinc-700 font-mono" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="visible"
          render={({ field }) => (
            <FormItem className="flex items-center gap-2">
              <FormLabel>Visible</FormLabel>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {defaultValues ? "Update FAQ" : "Add FAQ"}
        </Button>
      </form>
    </Form>
  );
}

export default function AdminFaqPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingFaq, setEditingFaq] = useState<FaqItem | null>(null);

  const { data: faqItems, isLoading } = useQuery<FaqItem[]>({
    queryKey: ["/api/admin/faq"],
    queryFn: async () => {
      const response = await fetch("/api/admin/faq");
      if (!response.ok) {
        throw new Error("Failed to fetch FAQ items");
      }
      return response.json();
    },
  });

  const addFaq = useMutation({
    mutationFn: async (data: FaqFormData) => {
      const res = await fetch("/api/admin/faq", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/faq"] });
      toast({ title: "FAQ item added successfully" });
    },
    onError: (error) => {
      toast({
        title: "Error adding FAQ item",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateFaq = useMutation({
    mutationFn: async (data: FaqFormData & { id: number }) => {
      const { id, ...faq } = data;
      const res = await fetch(`/api/admin/faq/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(faq),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/faq"] });
      toast({ title: "FAQ item updated successfully" });
      setEditingFaq(null);
    },
    onError: (error) => {
      toast({
        title: "Error updating FAQ item",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <AdminLayout>
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle>FAQ</CardTitle>
          <CardDescription>Manage FAQ entries</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <Dialog>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Add New FAQ
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800">
                <DialogHeader>
                  <DialogTitle>Add New FAQ</DialogTitle>
                </DialogHeader>
                <FaqForm 
                  onSubmit={(data) => addFaq.mutate(data)} 
                  isSubmitting={addFaq.isPending} 
                />
              </DialogContent>
            </Dialog>

            {isLoading ? (
              <div className="flex justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Question</TableHead>
                    <TableHead>Video ID</TableHead>
                    <TableHead>Visible</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {faqItems?.map((faq) => (
                    <TableRow key={faq.id}>
                      <TableCell className="font-medium">{faq.question}</TableCell>
                      <TableCell>
                        <code className="bg-zinc-800 px-2 py-1 rounded text-xs">
                          {faq.videoId}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={faq.visible}
                          onCheckedChange={(checked) => {
                            updateFaq.mutate({
                              ...faq,
                              visible: checked,
                            });
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Dialog open={editingFaq?.id === faq.id} onOpenChange={(open) => !open && setEditingFaq(null)}>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingFaq(faq)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-zinc-900 border-zinc-800">
                            <DialogHeader>
                              <DialogTitle>Edit FAQ</DialogTitle>
                            </DialogHeader>
                            <FaqForm 
                              defaultValues={faq}
                              onSubmit={(data) => updateFaq.mutate({ ...data, id: faq.id })}
                              isSubmitting={updateFaq.isPending}
                            />
                          </DialogContent>
                        </Dialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
